import React, { useState, useEffect } from 'react';
import '../Assets/Styles/ClientsPage.css';
import { searchClients } from '../Api/Clients';
import { getClientInvoices } from '../Api/Invoices';
import { useOrder } from '../Context/OrderContext';
import { useNavigate } from 'react-router-dom';
import { findProductByName } from '../Utils/IndexedDB';
import { parseBulkClients } from "../Utils/ClientsParser";

function ClientsPage() {
    const [query, setQuery] = useState('');
    const [allClients, setAllClients] = useState([]);
    const [filteredClients, setFilteredClients] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const { selectedClient, setSelectedClient } = useOrder();
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [errorInvoices, setErrorInvoices] = useState(null);
    const [itemTotals, setItemTotals] = useState([]);
    const [activeTab, setActiveTab] = useState('invoices');
    const [selectedPeriod, setSelectedPeriod] = useState('last_12_months');

    const {
        selectedProducts,
        setSelectedProducts,
        updateProduct,
        removeProduct,
    } = useOrder();

    const navigate = useNavigate();

    const [bulkText, setBulkText] = useState("");
    const [bulkClientMatches, setBulkClientMatches] = useState([]);


    // Fetch all clients on mount
    useEffect(() => {
        async function fetchAllClients() {
            try {
                const data = await searchClients('');
                setAllClients(data.clients || []);
            } catch (error) {
                console.error('Error fetching clients:', error);
            }
        }
        fetchAllClients();
    }, []);

    // Filter clients when query or allClients change
    useEffect(() => {
        if (query.trim() === '') {
            setFilteredClients([]);
            setShowSuggestions(false);
            return;
        }

        const results = allClients.filter(client =>
            client.name?.toLowerCase().includes(query.toLowerCase())
        );

        setFilteredClients(results);
        setShowSuggestions(true);
        setHighlightedIndex(-1);
    }, [query, allClients]);

    // Input change handler
    const handleInputChange = e => {
        setQuery(e.target.value);
        setShowSuggestions(true);
        setSelectedClient(null);
        setInvoices([]);
    };

    // Keyboard navigation for suggestions
    const handleKeyDown = e => {
        if (!showSuggestions || filteredClients.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev < filteredClients.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredClients.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedIndex >= 0 && filteredClients[highlightedIndex]) {
                const selected = filteredClients[highlightedIndex];
                setQuery(selected.name);
                setSelectedClient(selected);
                setShowSuggestions(false);
                document.activeElement.blur();
            }
        }
    };

    // When user clicks a client suggestion
    const handleClientSelect = client => {
        setQuery(client.name);
        setSelectedClient(client);
        setShowSuggestions(false);
    };


    // Navigate to products page
    const handleProceed = () => {
        if (selectedClient) {
            navigate('/products');
        }
    };


    // invoices
    useEffect(() => {
        if (!selectedClient) {
            setInvoices([]);
            setItemTotals([]);
            setErrorInvoices(null);
            setLoadingInvoices(false);
            return;
        }

        async function fetchInvoices() {
            setLoadingInvoices(true);
            setErrorInvoices(null);

            try {
                const data = await getClientInvoices({ client_id: selectedClient.id, period: selectedPeriod });

                let fetchedInvoices = [];

                if (Array.isArray(data.invoices)) fetchedInvoices = data.invoices;
                else if (Array.isArray(data)) fetchedInvoices = data;
                else fetchedInvoices = [];

                setInvoices(fetchedInvoices);

                // ✅ Extract and sum quantities by product ID
                const totals = {}; // key = product.id, value = { id, name, totalQuantity }

                for (const invoice of fetchedInvoices) {
                    if (Array.isArray(invoice.positions)) {
                        for (const item of invoice.positions) {
                            const name = item.name;
                            const qty = parseFloat(item.quantity) || 0;

                            const product = await findProductByName(name); // try matching product from IndexedDB
                            if (!product) continue; // skip if no match

                            if (totals[product.id]) {
                                totals[product.id].totalQuantity += qty;
                            } else {
                                totals[product.id] = {
                                    id: product.id,
                                    name: product.name,
                                    totalQuantity: qty
                                };
                            }
                        }
                    }
                }

                const totalsArray = Object.values(totals).sort((a, b) => b.totalQuantity - a.totalQuantity);

                setItemTotals(totalsArray);

            } catch (err) {
                setErrorInvoices(err.message || 'Failed to fetch invoices');
                setInvoices([]);
                setItemTotals([]);
            } finally {
                setLoadingInvoices(false);
            }
        }


        fetchInvoices();
    }, [selectedClient, selectedPeriod]);

    const handleSelect = async (summaryItem) => {
        try {
            const matchedProduct = await findProductByName(summaryItem.name);

            if (!matchedProduct) {
                console.warn('Product not found in IndexedDB:', summaryItem.name);
                return;
            }

            const existingIndex = selectedProducts.findIndex(p => p.id === matchedProduct.id);
            if (existingIndex !== -1) {
                updateProduct(matchedProduct.id, {
                    quantity: selectedProducts[existingIndex].quantity + 1
                });
            } else {
                setSelectedProducts(prev => [
                    ...prev,
                    {
                        id: matchedProduct.id,
                        name: matchedProduct.name,
                        price_net: parseFloat(String(matchedProduct.price_net).replace(',', '.')),
                        quantity_unit: matchedProduct.quantity_unit,
                        tax: parseFloat(String(matchedProduct.tax).replace(',', '.')),
                        quantity: 1,
                    },
                ]);
            }

        } catch (error) {
            console.error('Error selecting product:', error);
        }
    };


    useEffect(() => {
  if (!bulkText || allClients.length === 0) return;

  const results = parseBulkClients(bulkText, allClients);
  setBulkClientMatches(results);
}, [bulkText, allClients]);



    return (
        <div className="clients-container">
            {/* 🔍 Search Header */}
            <h2>🔍 Search Clients</h2>

            {/* 🔎 Client Search Input */}
            <input
                className="clients-input"
                type="text"
                placeholder="Start typing a client name..."
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} // Delay to allow click selection
                onFocus={() => query && filteredClients.length > 0 && setShowSuggestions(true)} // Show suggestions on focus if applicable
            />

            {/* 📜 Suggestions Dropdown */}
            {showSuggestions && (
                <ul className="suggestions-list">
                    {filteredClients.length > 0 ? (
                        filteredClients.map((client, index) => (
                            <li
                                key={client.id}
                                className={`suggestion-item ${index === highlightedIndex ? 'active' : ''}`}
                                onMouseDown={() => handleClientSelect(client)} // MouseDown prevents blur-before-click
                            >
                                {client.name}
                            </li>
                        ))
                    ) : (
                        <li className="no-result">No name found.</li>
                    )}
                </ul>
            )}
            {selectedClient && (
                <div className="selectedName-tag" style={{ margin: '3px 5px' }}>
                    <small>Selected Client: {selectedClient?.name}</small>
                </div>

            )}

            {/* ✅ After Client is Selected */}
            {selectedClient && (
                <>
                    {/* ✅ Selected client & proceed */}
                    <div className="proceed-btn-container">
                        <button onClick={handleProceed} className="proceed-btn">
                            ➡️ Proceed to Product List
                        </button>
                    </div>

                    {/* ✅ Tabs */}
                    <div className="tabs">
                        <button
                            className={activeTab === 'invoices' ? 'active-tab' : ''}
                            onClick={() => setActiveTab('invoices')}
                        >
                            📄 Invoices ({invoices.length})
                        </button>
                        <button
                            className={activeTab === 'summary' ? 'active-tab' : ''}
                            onClick={() => setActiveTab('summary')}
                        >
                            📦 Product Summary
                        </button>
                        <select
                            name='period'
                            className='period'
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                        >
                            <option value="last_30_days">Last 30 Days</option>
                            <option value="last_12_months">Last 12 Months</option>
                            <option value="this_year">This Year</option>
                            <option value="last_year">Last Year</option>
                        </select>
                    </div>

                    {/* ✅ Tab: Invoices FIRST */}
                    {activeTab === 'invoices' && (
                        <div className="invoice-section">
                            {loadingInvoices && <p>Loading...</p>}
                            {errorInvoices && <p style={{ color: 'red' }}>Error: {errorInvoices}</p>}
                            {!loadingInvoices && !errorInvoices && invoices.length === 0 && (
                                <p>No results found for this client.</p>
                            )}

                            {invoices.length > 0 && (
                                <div className="invoice-list">
                                    {invoices.map(({ id, number, sell_date, status, price_gross, view_url }) => (
                                        <div key={id} className="invoice-card">
                                            <div><strong>Number:</strong> {number}</div>
                                            <div><strong>Sell Date:</strong> {sell_date}</div>
                                            <div>
                                                <strong>Status:</strong>{' '}
                                                <span
                                                    style={{
                                                        background: status === 'issued' ? 'rgb(248, 80, 49)' : 'rgba(130, 188, 64, 1)',
                                                        color: 'white',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px'
                                                    }}
                                                >
                                                    {status}
                                                </span>
                                            </div>
                                            <div>
                                                <strong>Price Gross (€):</strong>{' '}
                                                {typeof price_gross === 'number' ? price_gross.toFixed(2) : price_gross}
                                            </div>
                                            <div>
                                                <a href={view_url} target="_blank" rel="noopener noreferrer">
                                                    Open
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ✅ Tab: Product Summary SECOND */}
                    {activeTab === 'summary' && itemTotals.length > 0 && (
                        <div className="client-summary">
                            <h3>📦 Product Summary for {selectedClient.name}</h3>
                            <ul>
                                {itemTotals
                                    .filter(item => item.totalQuantity > 0)
                                    .map((item) => {
                                        const isAdded = selectedProducts.some(p => p.id === item.id);

                                        return (
                                            <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span>{item.name} → <strong>{item.totalQuantity}</strong></span>
                                                <button
                                                    className={isAdded ? 'added-btn' : 'add-btn'}
                                                    onClick={() => handleSelect(item)}
                                                    disabled={isAdded}
                                                >
                                                    {isAdded ? 'Added' : 'Add'}
                                                </button>
                                            </li>
                                        );
                                    })}
                            </ul>
                        </div>
                    )}

                </>
            )}
        </div>
    );


}

export default ClientsPage;

